/**
 * Amend lab report (PROD-3204, PM slice 5, board N6): change one value, give a reason, save the next version. The
 * version before stays in the history. Behind `?x=intake-desk`.
 *
 * Pieces: the app `Dialog` at `--dialog-width-form`, the app `Input`, `Textarea` and `Button` (Cancel outline, Save
 * primary and last).
 */
import { useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/shared/api/api-client';
import { useAmendLabReport } from '@/scheduling/services/use-lab-request-reports';
import {
  AMEND_REASON_MAX,
  buildAmendBody,
  isCurrentValue,
  parseAmendValue,
  sameValue,
  testName,
  unitText,
  valueText,
} from '@/scheduling/lib/saved-lab-report';
import type { LabReportDto, LabReportObservationDto } from '@/types/lab-results-api';

export interface AmendLabReportDialogProps {
  patientId: string;
  labRequestId: string;
  report: LabReportDto;
  /** The value to change; null closes the dialog. */
  observation: LabReportObservationDto | null;
  onClose: () => void;
  onSaved: () => void;
}

const STALE_TEXT = 'Someone amended this report first. Close it, open it again, and make the change on the new version.';

function errorText(e: unknown): string {
  if (e instanceof ApiError && e.status === 409) return STALE_TEXT;
  if (e instanceof ApiError && e.status === 400) return 'The server did not accept this change. Check the value and the reason.';
  return 'The report could not be saved. Try again.';
}

export function AmendLabReportDialog({ patientId, labRequestId, report, observation, onClose, onSaved }: AmendLabReportDialogProps) {
  const amend = useAmendLabReport(patientId, labRequestId);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const valueId = useId();
  const reasonId = useId();
  const next = report.version + 1;

  const parsed = observation ? parseAmendValue(value, observation) : null;
  const unchanged = !!(parsed && observation && sameValue(parsed, observation));
  const valueProblem =
    value.trim() === '' ? null : !parsed ? 'Type a number, for example 321 or <0.5.' : unchanged ? 'This is the saved value.' : null;
  // A 409 means another amend landed: this form was built on the version before. Nothing more is saved from it.
  const conflict = amend.error instanceof ApiError && amend.error.status === 409;
  // The value is not on the version shown any more: a refetch brought a newer version (with or without a 409).
  const stale = conflict || (observation !== null && !isCurrentValue(report, observation.id));
  const ready = !!parsed && !unchanged && reason.trim() !== '' && !amend.isPending && !stale;

  const close = () => {
    if (amend.isPending) return;
    setValue('');
    setReason('');
    amend.reset();
    onClose();
  };

  const save = () => {
    if (!observation || !parsed || !ready) return;
    let body;
    try {
      body = buildAmendBody(report, observation.id, parsed, reason);
    } catch {
      return; // the value is not on this version any more: Save is off (stale) and the text says why
    }
    amend.mutate(
      { labReportId: report.id, body },
      {
        onSuccess: () => {
          setValue('');
          setReason('');
          onSaved();
        },
      },
    );
  };

  const unit = observation ? unitText(observation) : '';
  return (
    <Dialog open={observation !== null} onOpenChange={(open) => (open ? undefined : close())}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[var(--dialog-width-form)]">
        <DialogHeader>
          <DialogTitle className="[font:var(--type-card-title)]">Amend lab report</DialogTitle>
          <DialogDescription className="text-text-secondary [font:var(--type-body)]">
            Saves version {next}. Version {report.version} stays in the history.
          </DialogDescription>
        </DialogHeader>
        {observation ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-text-secondary [font:var(--type-label)]">Result</span>
              <span className="text-text-primary [font:var(--type-body)]">
                {testName(observation)}
                {unit ? ` · ${unit}` : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-text-secondary [font:var(--type-label)]">Saved value</span>
                <span className="tabular-nums text-text-primary [font:var(--type-body)]">{valueText(observation)}</span>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={valueId} className="[font:var(--type-label)]">
                  New value
                </Label>
                <Input
                  id={valueId}
                  className="tabular-nums"
                  value={value}
                  aria-invalid={valueProblem !== null}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>
            </div>
            {valueProblem ? (
              <p className="m-0 text-[var(--status-bad)] [font:var(--type-meta)]">{valueProblem}</p>
            ) : null}
            <div className="flex flex-col gap-2">
              <Label htmlFor={reasonId} className="[font:var(--type-label)]">
                Reason (required)
              </Label>
              <Textarea
                id={reasonId}
                rows={3}
                value={reason}
                maxLength={AMEND_REASON_MAX}
                onChange={(e) => setReason(e.target.value.slice(0, AMEND_REASON_MAX))}
              />
              <span className="text-text-muted [font:var(--type-meta)]">
                {reason.length} of {AMEND_REASON_MAX} characters
              </span>
            </div>
            {stale ? (
              <p role="alert" className="m-0 text-[var(--status-bad)] [font:var(--type-body)]">
                {STALE_TEXT}
              </p>
            ) : amend.isError ? (
              <p role="alert" className="m-0 text-[var(--status-bad)] [font:var(--type-body)]">
                {errorText(amend.error)}
              </p>
            ) : null}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={amend.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!ready}>
            {amend.isPending ? 'Saving…' : `Save version ${next}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
